import React from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import { PROMOTIONS_ROWS_PER_PAGE } from './constants';
import PromotionEnvBadge from './PromotionEnvBadge';
import PromotionStatusChip from './PromotionStatusChip';
import type { OutgoingPromotionRow } from './types';

interface OutgoingPromotionsTableProps {
  rows: OutgoingPromotionRow[];
  isError: boolean;
  onView: (requestUuid: string) => void;
}

/** The source-side outgoing promotions table. Read-only actions (View) — source-side cancel is deferred
 * to the X-phase. */
const OutgoingPromotionsTable: React.FC<OutgoingPromotionsTableProps> = ({ rows, isError, onView }) => {
  // The target env is blank for same-DB rows (the backend only populates it for cross-deployment pairings);
  // show the column only when at least one row is cross-deployment, so an all-same-DB list is unchanged.
  const hasTargetEnv = rows.some((row) => Boolean(row.target_deployment_id));
  const columns = [
    {
      id: 'request_uuid',
      label: 'Promotion ID',
      specialCell: (row: OutgoingPromotionRow) => <span className="font-mono text-slate-900">{row.request_uuid}</span>,
    },
    {
      id: 'request_name',
      label: 'Name',
      specialCell: (row: OutgoingPromotionRow) => <span>{row.request_name || '—'}</span>,
    },
    { id: 'target_org_external_id', label: 'Target' },
    ...(hasTargetEnv
      ? [
          {
            id: 'target_deployment_id',
            label: 'Target env',
            specialCell: (row: OutgoingPromotionRow) => <PromotionEnvBadge subDomain={row.target_deployment_id} />,
          },
        ]
      : []),
    {
      id: 'cached_status',
      label: 'Status',
      specialCell: (row: OutgoingPromotionRow) => <PromotionStatusChip status={row.cached_status} />,
    },
    {
      id: 'requesting_user_display_name',
      label: 'Requested by',
      specialCell: (row: OutgoingPromotionRow) => (
        <span>{row.requesting_user_display_name ?? row.requesting_user_id}</span>
      ),
    },
    {
      id: 'created_at',
      label: 'Created',
      specialCell: (row: OutgoingPromotionRow) => <span>{serverDateTimeToLocal(row.created_at)}</span>,
    },
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (row: OutgoingPromotionRow) => (
        <Button variant="text" color="primary" onClick={() => onView(row.request_uuid)}>
          View
        </Button>
      ),
      disableSort: true,
    },
  ];

  if (isError) {
    return <SkeletonTable rowsCount={3} columnsCount={columns.length} isError />;
  }

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      defaultOrderColumn={columns.findIndex((c) => c.id === 'created_at')} // Created (index shifts with the optional Target-env column)
      order="desc"
      stickyHeader
      emptyStateComponent={<EmptyState title="No outgoing promotions" />}
      autoPaginateRowsPerPage={PROMOTIONS_ROWS_PER_PAGE}
    />
  );
};

export default OutgoingPromotionsTable;
